import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import warehouseService from '../../service/warehouse.service';
import { WarehouseReports } from './WarehouseReports';
import { WarehouseFields } from './WarehouseFields';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';

export const WarehousePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    companyLocation: [],
  });

  const def = {
    code: '',
    name: '',
    companyLocationId: null,
    binManaged: false,
    binLevel: 1,
    binSeparator: '',
    level1Mode: 'numeric',
    level1Padding: null,
    level2Mode: 'numeric',
    level2Padding: null,
    level3Mode: 'numeric',
    level3Padding: null,
    level4Mode: 'numeric',
    level4Padding: null,
    level5Mode: 'numeric',
    level5Padding: null,
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('warehouse');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    masterDataService
      .companyLocationToForm()
      .then((data) => {
        const aData = {
          companyLocation: data,
        };

        const acOptions = {
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
        };
        setAutocompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(),
    name: Yup.string().required(),
    companyLocationId: Yup.string().nullable(),
    binManaged: Yup.boolean(),
    binLevel: Yup.number().integer().min(1).max(5).nullable(),
    binSeparator: Yup.string().nullable(),
    level1Mode: Yup.string().nullable(),
    level1Padding: Yup.number().integer().nullable(),
    level2Mode: Yup.string().nullable(),
    level2Padding: Yup.number().integer().nullable(),
    level3Mode: Yup.string().nullable(),
    level3Padding: Yup.number().integer().nullable(),
    level4Mode: Yup.string().nullable(),
    level4Padding: Yup.number().integer().nullable(),
    level5Mode: Yup.string().nullable(),
    level5Padding: Yup.number().integer().nullable(),
  });

  const valuesToForm = () => {
    const {
      code,
      name,
      companyLocationId,
      binManaged,
      binLevel,
      binSeparator,
      level1Mode,
      level1Padding,
      level2Mode,
      level2Padding,
      level3Mode,
      level3Padding,
      level4Mode,
      level4Padding,
      level5Mode,
      level5Padding,
    } = selectedValues;

    setInitValues({
      code,
      name,
      companyLocationId,
      binManaged,
      binLevel,
      binSeparator: binSeparator ?? '',
      level1Mode,
      level1Padding,
      level2Mode,
      level2Padding,
      level3Mode,
      level3Padding,
      level4Mode,
      level4Padding,
      level5Mode,
      level5Padding,
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
    };

    warehouseService.updateWarehouse(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
    };

    warehouseService
      .createWarehouse(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    warehouseService.deleteWarehouse(deleteData, selectedValues.code).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="Warehouse"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <WarehouseFields
                      viewField={viewField}
                      selectedValuesState={{ selectedValues, setSelectedValues }}
                      addField={addField}
                      autocompleteOptions={autocompleteOptions}
                      updateWhs={updateObject}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <WarehouseReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autocompleteOptions={autocompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
